import { useState, useEffect } from 'react';
import { formatCurrency } from '../lib/format';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Package2,
  ArrowLeft,
  Clock,
  CheckCircle,
  XCircle,
  Truck,
  DollarSign,
  AlertCircle,
  FileText,
  User,
  Building2,
  MapPin,
  Check,
  X
} from 'lucide-react';
import { useUser } from '../store/useAuthStore';
import { apiClient } from '../lib/api';
import { BulkOrderStatus } from '@bloom/shared';
import { toast } from '../lib/toast';
import { formatDateTime } from '../utils/dateUtils';
import { OrderPharmacyBadge } from '../components/pharmacy/PharmacyBadge';
import { itemsWithPeptide } from '../lib/pharmacyRouting';
import { useProductPeptideMap } from '../hooks/useProductPeptideMap';

interface StatusConfig {
  label: string;
  color: string;
  bgColor: string;
  icon: React.ComponentType<any>;
}

const statusConfig: Record<BulkOrderStatus, StatusConfig> = {
  draft: { label: 'Draft', color: 'text-gray-600', bgColor: 'bg-gray-100', icon: FileText },
  pending_approval: { label: 'Pending Approval', color: 'text-yellow-600', bgColor: 'bg-yellow-100', icon: Clock },
  awaiting_payment: { label: 'Awaiting Payment', color: 'text-orange-600', bgColor: 'bg-orange-100', icon: DollarSign },
  paid: { label: 'Paid', color: 'text-green-600', bgColor: 'bg-green-100', icon: CheckCircle },
  processing: { label: 'Processing', color: 'text-blue-600', bgColor: 'bg-blue-100', icon: Package2 },
  shipped: { label: 'Shipped', color: 'text-purple-600', bgColor: 'bg-purple-100', icon: Truck },
  delivered: { label: 'Delivered', color: 'text-green-700', bgColor: 'bg-green-200', icon: CheckCircle },
  voided: { label: 'Voided', color: 'text-gray-600', bgColor: 'bg-gray-100', icon: XCircle },
  cancelled: { label: 'Cancelled', color: 'text-red-600', bgColor: 'bg-red-100', icon: XCircle }
};

export const BulkOrderDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const user = useUser();
  const [order, setOrder] = useState<any>(null);
  // productId → isPeptide, so the header shows which pharmacy fills this order.
  const peptideMap = useProductPeptideMap(
    (order?.items ?? []).map((i: any) => String(i.productId))
  );
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => {
    if (id) {
      loadOrder();
    }
  }, [id]);

  const loadOrder = async () => {
    try {
      setLoading(true);
      const response = await apiClient.getBulkOrder(id!);
      if (response.success && response.data) {
        setOrder(response.data);
      } else {
        toast.error('Failed to load order');
        navigate('/bulk-orders/list');
      }
    } catch (error) {
      console.error('Failed to load order:', error);
      toast.error('Failed to load order');
      navigate('/bulk-orders/list');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    try {
      setApproving(true);
      const response = await apiClient.approveBulkOrder(id!);
      if (response.success) {
        toast.success('Order approved! Invoice has been created.');
        loadOrder();
      } else {
        toast.error(response.error || 'Failed to approve order');
      }
    } catch (error) {
      console.error('Failed to approve order:', error);
      toast.error('Failed to approve order');
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async () => {
    try {
      setRejecting(true);
      const response = await apiClient.rejectBulkOrder(id!, rejectReason || undefined);
      if (response.success) {
        toast.success('Order rejected');
        setShowRejectModal(false);
        loadOrder();
      } else {
        toast.error(response.error || 'Failed to reject order');
      }
    } catch (error) {
      console.error('Failed to reject order:', error);
      toast.error('Failed to reject order');
    } finally {
      setRejecting(false);
    }
  };


  const formatDate = (date: string | Date) => formatDateTime(date);

  const getStatusBadge = (status: BulkOrderStatus) => {
    const config = statusConfig[status];
    if (!config) return null;

    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${config.bgColor} ${config.color}`}>
        <Icon className="w-4 h-4" />
        {config.label}
      </span>
    );
  };

  const canApproveOrReject = () => {
    if (!order || !user) return false;
    // Only physician can approve/reject orders pending their approval
    // Handle both populated and non-populated physicianId
    const orderPhysicianId = order.physicianId?._id?.toString() || order.physicianId?.toString();
    const userId = user._id?.toString();
    return (
      (user.role === 'physician' || user.role === 'physician_readonly') &&
      order.status === 'pending_approval' &&
      orderPhysicianId === userId
    );
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-gray-600">Please log in to view this order.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-500">Loading order details...</p>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center py-12">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-gray-600">Order not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Back Button */}
      <button
        onClick={() => navigate('/bulk-orders/list')}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Orders
      </button>

      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Package2 className="w-6 h-6 text-blue-600" />
              <h1 className="text-xl font-bold text-gray-900">{order.bulkOrderNumber}</h1>
              {getStatusBadge(order.status)}
              <OrderPharmacyBadge
                items={itemsWithPeptide(order.items, peptideMap)}
                shipToState={order.fulfillmentDetails?.address?.state}
                size="sm"
              />
            </div>
            <p className="text-sm text-gray-500">
              Created {formatDate(order.createdAt)}
            </p>
          </div>

          {/* Action Buttons for Pending Approval */}
          {canApproveOrReject() && (
            <div className="flex gap-3">
              <button
                onClick={() => setShowRejectModal(true)}
                disabled={rejecting || approving}
                className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 flex items-center gap-2 disabled:opacity-50"
              >
                <X className="w-4 h-4" />
                Reject
              </button>
              <button
                onClick={handleApprove}
                disabled={approving || rejecting}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2 disabled:opacity-50"
              >
                {approving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    Approving...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Approve Order
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        {/* Pending Approval Message */}
        {order.status === 'pending_approval' && canApproveOrReject() && (
          <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-yellow-600 mt-0.5" />
              <div>
                <h3 className="font-medium text-yellow-800">This order requires your approval</h3>
                <p className="text-sm text-yellow-700 mt-1">
                  Review the order details below and approve or reject. You can also modify the order before approving.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Rejection Info */}
        {order.status === 'cancelled' && order.rejectedAt && (
          <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <XCircle className="w-5 h-5 text-red-600 mt-0.5" />
              <div>
                <h3 className="font-medium text-red-800">Order Rejected</h3>
                <p className="text-sm text-red-700 mt-1">
                  Rejected on {formatDate(order.rejectedAt)}
                  {order.rejectionReason && (
                    <span className="block mt-1">Reason: {order.rejectionReason}</span>
                  )}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Order Details Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Physician Info */}
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <h3 className="text-sm font-medium text-gray-500 mb-3 flex items-center gap-2">
            <User className="w-4 h-4" />
            Physician
          </h3>
          {order.physicianId?.profile ? (
            <div>
              <p className="font-medium text-gray-900">
                Dr. {order.physicianId.profile.firstName} {order.physicianId.profile.lastName}
              </p>
              <p className="text-sm text-gray-500">{order.physicianId.email}</p>
              {order.physicianId.profile.clinicName && (
                <p className="text-sm text-gray-500">{order.physicianId.profile.clinicName}</p>
              )}
            </div>
          ) : (
            <p className="text-gray-500">Unknown</p>
          )}
        </div>

        {/* Created By Info */}
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <h3 className="text-sm font-medium text-gray-500 mb-3 flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            Created By
          </h3>
          {order.createdById?.profile ? (
            <div>
              <p className="font-medium text-gray-900">
                {order.createdById.profile.firstName} {order.createdById.profile.lastName}
              </p>
              <p className="text-sm text-gray-500">{order.createdById.email}</p>
              <p className="text-xs text-gray-400 mt-1">
                Role: {order.createdByRole === 'distributor' ? 'Account Manager' : 'Physician'}
              </p>
            </div>
          ) : (
            <p className="text-gray-500">Unknown</p>
          )}
        </div>

        {/* Fulfillment Info */}
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <h3 className="text-sm font-medium text-gray-500 mb-3 flex items-center gap-2">
            <MapPin className="w-4 h-4" />
            Fulfillment
          </h3>
          <p className="font-medium text-gray-900 capitalize">
            {order.fulfillmentDetails?.type?.replace('_', ' ') || 'Not specified'}
          </p>
          {order.fulfillmentDetails?.address && (
            <div className="text-sm text-gray-500 mt-1">
              <p>{order.fulfillmentDetails.address.street}</p>
              {order.fulfillmentDetails.address.street2 && (
                <p>{order.fulfillmentDetails.address.street2}</p>
              )}
              <p>
                {order.fulfillmentDetails.address.city}, {order.fulfillmentDetails.address.state}{' '}
                {order.fulfillmentDetails.address.zipCode}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Order Items */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden mb-6">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Order Items</h2>
        </div>
        <div className="divide-y divide-gray-200">
          {/* Medication items */}
          {order.items?.filter((item: any) => !item.isSupply).map((item: any, index: number) => (
            <div key={`med-${index}`} className="px-6 py-4 flex items-center justify-between">
              <div className="flex-1">
                <p className="font-medium text-gray-900">{item.productName}</p>
                <p className="text-sm text-gray-500">
                  {formatCurrency(item.unitPrice)} × {item.quantity}
                </p>
              </div>
              <p className="font-semibold text-gray-900">{formatCurrency(item.totalPrice)}</p>
            </div>
          ))}
          {/* Supply items */}
          {order.items?.some((item: any) => item.isSupply) && (
            <>
              <div className="px-6 py-2 bg-amber-50 border-t border-amber-100">
                <p className="text-xs font-medium text-amber-700 uppercase tracking-wide">Auto-Added Supplies</p>
              </div>
              {order.items?.filter((item: any) => item.isSupply).map((item: any, index: number) => (
                <div key={`supply-${index}`} className="px-6 py-4 flex items-center justify-between bg-amber-50/50">
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{item.productName}</p>
                    <p className="text-sm text-gray-500">
                      {formatCurrency(item.unitPrice)} × {item.quantity}
                      {item.unitPrice === 0 && <span className="ml-2 text-green-600 font-medium">Free</span>}
                    </p>
                  </div>
                  <p className="font-semibold text-gray-900">{formatCurrency(item.totalPrice)}</p>
                </div>
              ))}
            </>
          )}
        </div>
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
          <div className="flex justify-between items-center">
            <span className="text-lg font-semibold text-gray-900">Total</span>
            <span className="text-xl font-bold text-gray-900">{formatCurrency(order.totalAmount)}</span>
          </div>
        </div>
      </div>

      {/* Invoice Link */}
      {order.invoiceId && (
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FileText className="w-5 h-5 text-blue-600" />
              <div>
                <p className="font-medium text-gray-900">
                  Invoice: {order.invoiceId.invoiceNumber || order.invoiceId._id}
                </p>
                <p className="text-sm text-gray-500">
                  Status: {order.invoiceId.status || 'Unknown'}
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                const invoiceId = order.invoiceId._id || order.invoiceId;
                if (user?.role === 'admin') {
                  navigate(`/admin/invoices?invoiceId=${invoiceId}`);
                } else {
                  navigate(`/invoice-tracking?invoiceId=${invoiceId}`);
                }
              }}
              className="px-3 py-1.5 text-blue-600 hover:bg-blue-50 rounded-lg text-sm"
            >
              View Invoice
            </button>
          </div>
        </div>
      )}

      {/* Tracking Information */}
      {order.trackingInfo && (order.trackingInfo.trackingNumber || order.trackingInfo.trackingUrl) && (
        <div className="bg-white rounded-lg shadow-sm border p-4 mt-6">
          <h3 className="text-sm font-medium text-gray-500 mb-3 flex items-center gap-2">
            <Truck className="w-4 h-4" />
            Tracking Information
          </h3>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {order.trackingInfo.carrier && (
                <div>
                  <p className="text-xs text-blue-600 font-medium">Carrier</p>
                  <p className="text-sm font-medium text-gray-900">{order.trackingInfo.carrier}</p>
                </div>
              )}
              {order.trackingInfo.trackingNumber && (
                <div>
                  <p className="text-xs text-blue-600 font-medium">Tracking Number</p>
                  <p className="text-sm font-medium text-gray-900 font-mono">{order.trackingInfo.trackingNumber}</p>
                </div>
              )}
              {order.trackingInfo.trackingUrl && (
                <div>
                  <p className="text-xs text-blue-600 font-medium">Track Package</p>
                  <a
                    href={order.trackingInfo.trackingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:text-blue-800 underline"
                  >
                    View Tracking →
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setShowRejectModal(false)} />
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Reject Order</h3>
              <p className="text-sm text-gray-600 mb-4">
                Are you sure you want to reject this order? This action cannot be undone.
              </p>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason (optional)
                </label>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Enter a reason for rejecting this order..."
                  rows={3}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowRejectModal(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReject}
                  disabled={rejecting}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2 disabled:opacity-50"
                >
                  {rejecting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      Rejecting...
                    </>
                  ) : (
                    <>
                      <XCircle className="w-4 h-4" />
                      Reject Order
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkOrderDetail;
